import { ChevronDown } from 'lucide-react';
import LinkButton from '|/components/carvana-ds/link-button';
import React, { useState } from 'react';
import CreditAppliedModal from '|/components/carvana-ds/credit-applied-modal';
import { useSlate } from '|/context';
import useOrderSummaryVm from '../../use-order-summary-vm';
import currency from '|/helpers/currency';
import percent from '|/helpers/percent';
import styles from './loan-details.module.css';
import { ReviewSummaryResponse } from '|/services/purchase-api';

interface LoanDetailsProps {
  summary: ReviewSummaryResponse;
}
const LoanDetails: React.FC<LoanDetailsProps> = ({ summary }) => {
  const {
    isCarvanaFinancing,
    isOutsideFinance,
    isThirdParty,
    hasPaymentMethodBeenSelected,
    hasTerms,
    amountFinanced,
    loanApr: apr,
    loanTerm: term,
    loanPayment: monthlyPayment,
    dueAtOrder,
    refundDue,
    enrolledInAutopay,
    cashDownAmount,
    shippingFee,
    dueAmount,
    lenderName,
    fundingInstitution: institution,
    dueLabel,
    dueLabelTotalSummary,
    creditFromPreviousOrder,
    thirdPartyLoanAmount
  } = useOrderSummaryVm(summary);
  const { isSlateVehicle } = useSlate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const isLoan = isCarvanaFinancing || isThirdParty || isOutsideFinance;
  const showCreditAppliedLink = !!creditFromPreviousOrder;

  if (!hasPaymentMethodBeenSelected) return null;

  const basePaymentVerbiage = isLoan
    ? 'Cash down payment'
    : shippingFee
      ? 'Subtotal'
      : 'Total purchase price';

  const dropdownItems = [
    { title: basePaymentVerbiage, id: 'down', amount: cashDownAmount },
    { title: 'Nonrefundable shipping', id: 'shipping', amount: shippingFee },
    { title: 'Credit from previous order', id: 'refund', amount: creditFromPreviousOrder }
  ].filter(x => Boolean(x.amount));
  const hasDropdown = dropdownItems.length > 0;
  const hasMonthlyPayment = monthlyPayment && monthlyPayment > 0;

  const dueDisplayLabel = isThirdParty || isOutsideFinance ? dueLabel : dueLabelTotalSummary;
  const dueDisplayAmount = refundDue ? dueAmount : dueAtOrder;
  const lenderDisplayName = isCarvanaFinancing ? '' : lenderName || institution || '';

  const getLoanInfoText = () => {
    if (!apr || !term) return '';

    const aprText = percent(apr);
    const termText = `${term} months`;
    const discountText = enrolledInAutopay
      ? ' and a $10/mo discount\nfor enrolling in auto pay'
      : '';

    return [`Includes ${aprText} APR for ${termText}`, discountText, '.'].join('');
  };

  return (
    <div className={styles.innerContent}>
      <div className={styles.loanDetails}>
        {lenderDisplayName && (
          <div className={styles.lenderSection}>
            <h4>Lender</h4>
            <p>{lenderDisplayName}</p>
          </div>
        )}

        {isOutsideFinance ||
          (isThirdParty && (
            <div className={styles.monthlyPaymentSection}>
              <div className={styles.itemRow}>
                <h4>Financed with lender</h4>
                <h4>{currency(thirdPartyLoanAmount ?? 0)}</h4>
              </div>

              {/* Loan details Info */}
              {apr && term && <p className={styles.loanInfoText}>{getLoanInfoText()}</p>}
            </div>
          ))}

        {hasMonthlyPayment && isCarvanaFinancing && (
          <div className={styles.monthlyPaymentSection}>
            <div className={styles.itemRow}>
              <h4>Monthly payment</h4>
              <h4>{`${currency(monthlyPayment, { minimumFractionDigits: 0 })}/mo`}</h4>
            </div>

            {/* Loan details Info */}
            {apr && term && <p className={styles.loanInfoText}>{getLoanInfoText()}</p>}
          </div>
        )}

        {hasTerms && amountFinanced && isCarvanaFinancing && (
          <div className={styles.itemRow}>
            <h4>Total amount financed</h4>
            <p className={styles.lesserAmount}>{currency(amountFinanced)}</p>
          </div>
        )}

        <div className={styles.totalDueSection}>
          <div className={styles.itemRow}>
            <h4
              className={styles.expanderHeader}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              data-testid="total-due-expander"
            >
              <span>{dueDisplayLabel}</span>
              {hasDropdown && (
                <ChevronDown
                  style={{ transform: isDropdownOpen ? 'rotate(180deg)' : undefined }}
                  size={16}
                  color={isSlateVehicle ? 'var(--icon-primary)' : 'var(--icon-default)'}
                />
              )}
            </h4>
            <p className={styles.lesserAmount}>{currency(dueDisplayAmount ?? 0)}</p>
          </div>
          {hasDropdown && isDropdownOpen && (
            <div className={styles.dropdownContent}>
              {dropdownItems.map(item => (
                <div className={styles.itemRow} key={item.id}>
                  <p>{item.title}</p>
                  <p>
                    {item.id === 'refund' ? '-' : ''}
                    {currency(item.amount)}
                  </p>
                </div>
              ))}
              {showCreditAppliedLink && (
                <LinkButton
                  size="sm"
                  underline={false}
                  onClick={() => {
                    setIsCreditModalOpen(true);
                  }}
                  data-analytics-id="pp_checkout_order_summary_open_credit_modal"
                  className={styles.viewDetailsLink}
                >
                  View details
                </LinkButton>
              )}
            </div>
          )}
        </div>
      </div>
      {showCreditAppliedLink && (
        <CreditAppliedModal
          isOpen={isCreditModalOpen}
          onClose={() => {
            setIsCreditModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default LoanDetails;
